import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@shared/components/ui/chart";
import type { BillingPlan, MrrPoint } from "@shared/lib/admin-billing";
import { formatCents } from "@shared/lib/format";

/**
 * MRR de fin de mois, empilé par plan payant : la hauteur totale est le MRR,
 * chaque couche dit d'où il vient. Couleurs d'identité (`--chart-*`), dans
 * l'ordre des plans.
 */
const monthLabel = new Intl.DateTimeFormat("fr-FR", { month: "short", year: "2-digit", timeZone: "UTC" });

export function MrrChart({ points, plans }: { points: MrrPoint[]; plans: BillingPlan[] }) {
  const paid = plans.filter((plan) => plan.monthly_price_cents > 0);
  const config = Object.fromEntries(
    paid.map((plan, index) => [plan.id, { label: plan.name, color: `var(--chart-${(index % 5) + 1})` }]),
  ) satisfies ChartConfig;
  const data = points.map((point) => ({
    label: monthLabel.format(new Date(`${point.month}-01T00:00:00Z`)),
    accounts: point.accounts,
    ...Object.fromEntries(paid.map((plan) => [plan.id, (point.plans[plan.id] ?? 0) / 100])),
  }));

  return (
    <ChartContainer className="aspect-auto h-[240px] w-full" config={config}>
      <AreaChart accessibilityLayer data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis axisLine={false} dataKey="label" tickLine={false} tickMargin={8} />
        <YAxis
          axisLine={false}
          tickFormatter={(value: number) => formatCents(value * 100)}
          tickLine={false}
          width={64}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent formatter={(value, name) => (
              <span className="flex w-full justify-between gap-4">
                <span className="text-muted-foreground">{config[String(name)]?.label ?? name}</span>
                <span className="font-medium tabular-nums">{formatCents(Number(value) * 100)}</span>
              </span>
            )} />
          }
        />
        {paid.map((plan) => (
          <Area
            dataKey={plan.id}
            fill={`var(--color-${plan.id})`}
            fillOpacity={0.12}
            key={plan.id}
            stackId="mrr"
            stroke={`var(--color-${plan.id})`}
            strokeWidth={2}
            type="monotone"
          />
        ))}
        <ChartLegend content={<ChartLegendContent />} />
      </AreaChart>
    </ChartContainer>
  );
}
